import * as React from 'react';
import { I18n } from 'react-redux-i18n';

import { IconLink, ButtonAdd } from './styles';
import ButtonRemove from '../../ButtonRemove';
import { LinkType } from '../../../../../containers/Editor/constants';
import { LinkEmailData, LinkGoToPageData, LinkUrlData } from '../../../../../containers/Editor/utils/helpers';
import Utils from '../../../../../utils';
import ValidationUtils from '../../../../../utils/validation';
import { InputError } from '../../styles';

export type linkDataUsingInput = LinkUrlData | LinkEmailData | LinkGoToPageData | null;

interface LinkPanelInputProps {
	linkData: linkDataUsingInput;
	type: LinkType;
	onChange: (value: linkDataUsingInput) => void;
	spreads?: number[][];
	canAddLink?: boolean;
	onFocus?: () => void;
	onBlur?: () => void;
}

const isValidValue = (value: string, totalSpreads: number): { [k: string]: boolean } => ({
	[LinkType.url]: ValidationUtils.isValidUrl(value),
	[LinkType.mailto]: value.includes('@'),
	[LinkType.goToPage]: +value > 0 && +value <= totalSpreads,
});

const errorMessages: { [k: string]: string } = {
	[LinkType.url]: 'error.invalid_url',
	[LinkType.mailto]: 'error.invalid_email_address',
	[LinkType.goToPage]: 'error.invalid_page_number',
};

const getPlaceholder = (type: LinkType): string => {
	switch (type) {
		case LinkType.goToPage:
			return I18n.t('messages.page');
		case LinkType.mailto:
			return I18n.t('messages.email');
		case LinkType.url:
		default:
			return 'https://yourlink.com';
	}
};

const getInputValueFromLinkData = (linkData: linkDataUsingInput) => {
	return linkData ? linkData.data.href : '';
};

const LinkPanelInput = ({
	linkData,
	type,
	spreads = [],
	canAddLink = true,
	onChange,
	onFocus,
	onBlur,
}: LinkPanelInputProps) => {
	const initialInputValue = getInputValueFromLinkData(linkData);
	const [inputValue, setInputValue] = React.useState(initialInputValue);
	const [isError, setIsError] = React.useState(false);

	const handleInputChange = ({ target: { value } }) => {
		setInputValue(value);
		setIsError(!!value && !isValidValue(value, spreads.length)[type]);
	};

	const handleButtonAddClick = () => {
		onChange(getNewLinkData());
	};

	const getNewLinkData = (): linkDataUsingInput => {
		switch (type) {
			case LinkType.goToPage:
				const spreadIndex = +inputValue - 1;
				const correctedValue = spreads[spreadIndex][0].toString();
				return {
					type,
					data: {
						href: inputValue,
						correctedHref: correctedValue,
					}
				};
			case LinkType.mailto:
			case LinkType.url:
				return {
					type,
					data: {
						href: inputValue,
					},
				} as LinkEmailData | LinkUrlData;
			case LinkType.popup:
			case LinkType.unsupported:
			default:
				return null;
		}
	};

	const handleButtonRemoveClick = () => {
		setInputValue('');
		setIsError(false);

		if (linkData) {
			onChange(null);
		}
	};

	React.useEffect(() => {
		// update state when value from props was changed (when switch between links)
		const inputValue = getInputValueFromLinkData(linkData);
		setInputValue(inputValue);
		setIsError(!!inputValue && !isValidValue(inputValue, spreads.length)[type]);
	}, [linkData, type]);

	return (
		<>
			{isError && <InputError value={errorMessages[type]} />}
			<IconLink>
				{Utils.getSvgIcon('iconset_uprez_link')}
			</IconLink>
			<input
				type="text"
				value={inputValue}
				placeholder={getPlaceholder(type)}
				disabled={!linkData && !canAddLink}
				onChange={handleInputChange}
				onFocus={onFocus}
				onBlur={onBlur}
			/>
			<ButtonAdd
				type="button"
				disabled={!inputValue || isError}
				onClick={handleButtonAddClick}
			>
				{Utils.getSvgIcon('iconset_check-1-bold')}
			</ButtonAdd>
			<ButtonRemove
				onClick={handleButtonRemoveClick}
				isDisabled={!inputValue}
			/>
		</>
	);
};

export default LinkPanelInput;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/EditPanel/LinkPanel/LinkPanelInput/index.tsx